import type { TFunction } from "react-i18next"
import type { NavigateFunction } from "react-router-dom"
import {
  companyPreferencesHiringTemplateDisplayPath,
  companyPreferencesCreateHiringTemplatePath,
} from "../../utils/routeHelpers"
import {
  companyTemplateDetailsModalPath,
  companyGetFTEWizardPath,
} from "../../../full-time-employee"
import type { Company, CountryIsoCode } from "@oysterhr/schema"
import { EmploymentTemplateStatus } from "@oysterhr/schema"
import type { HiringTemplateCardFragment, TemplateButtons } from "./HiringTemplateCard"
import { idToStrongId } from "@oysterhr/common"
import { assertUnreachable } from "~/app/utils/typeUtils"

type TemplateCardActionsProps = {
  t: TFunction
  companyId?: string
  hiringTemplate: HiringTemplateCardFragment
  navigate: NavigateFunction
  countryCode?: CountryIsoCode
  subscriptionPlanId?: string
  employmentId?: string
}

export const getCompanyPreferencesTemplateCardActions = ({
  hiringTemplate,
  navigate,
  t,
}: TemplateCardActionsProps): TemplateButtons => {
  switch (hiringTemplate.status) {
    case EmploymentTemplateStatus.Active:
      return {
        secondary: {
          text: t("hiring_templates/HiringTemplateCard:buttons.viewDetails"),
          variation: "secondary",
          onClick: () => {
            navigate(
              companyPreferencesHiringTemplateDisplayPath(
                hiringTemplate.company.id,
                hiringTemplate.id || "",
              ),
            )
          },
        },
      }
    case EmploymentTemplateStatus.Draft:
      return {
        primary: {
          text: t("hiring_templates/HiringTemplateCard:buttons.edit"),
          variation: "primary",
          onClick: () => {
            navigate(
              companyPreferencesCreateHiringTemplatePath(
                hiringTemplate.company.id,
                hiringTemplate.id || "",
              ),
            )
          },
        },
      }
    default:
      return assertUnreachable(hiringTemplate.status)
  }
}

export const getCompanyNewHireTemplateCardActions = ({
  companyId,
  hiringTemplate,
  navigate,
  countryCode,
  subscriptionPlanId,
  employmentId,
  t,
}: TemplateCardActionsProps): TemplateButtons => {
  const hiringTemplateCompanyId = hiringTemplate?.company?.id || companyId || ""

  const wizardPathQuery = {
    countryCode,
    subscriptionPlanId,
    ...(hiringTemplate.oysterScore && { oysterScore: hiringTemplate.oysterScore }),
    ...(hiringTemplate.id && { templateId: hiringTemplate.id && idToStrongId(hiringTemplate.id) }),
  }

  const templateDetailsQuery = {
    countryCode,
    subscriptionPlanId,
    ...(hiringTemplate.oysterScore && { oysterScore: hiringTemplate.oysterScore }),
  }

  switch (hiringTemplate.status) {
    case EmploymentTemplateStatus.Active:
      return {
        primary: {
          text: t("hiring_templates/HiringTemplateCard:buttons.use"),
          variation: "primary",
          onClick: () => {
            navigate(
              companyGetFTEWizardPath(hiringTemplateCompanyId, {
                employmentId,
                query: wizardPathQuery,
              }),
            )
          },
        },
        secondary: {
          text: t("hiring_templates/HiringTemplateCard:buttons.viewDetails"),
          variation: "secondary",
          onClick: () => {
            navigate(
              companyTemplateDetailsModalPath(
                hiringTemplateCompanyId,
                hiringTemplate.id || "oyster-template",
                templateDetailsQuery,
              ),
            )
          },
        },
      }
    case EmploymentTemplateStatus.Draft:
      return {}
    default:
      return assertUnreachable(hiringTemplate.status)
  }
}

export const getExampleCardProps = (
  t: TFunction,
  company: Pick<Company, "id" | "name">,
): HiringTemplateCardFragment => ({
  id: "FAKE_ID",
  name: t("exampleTemplate.name", {
    companyName: company?.name ?? "",
    ns: "hiring_templates/HiringTemplateCard",
  }),
  description: t("exampleTemplate.description", { ns: "hiring_templates/HiringTemplateCard" }),
  countryCode: null,
  status: EmploymentTemplateStatus.Draft,
  company: {
    id: company.id,
    name: company.name,
  },
})

export const getExampleCardAction = ({
  company,
  navigate,
  t,
}: Pick<TemplateCardActionsProps, "navigate" | "t"> & {
  company: Pick<Company, "id" | "name">
}): TemplateButtons => {
  return {
    primary: {
      text: t("buttons.explore", { ns: "hiring_templates/HiringTemplateCard" }),
      variation: "primary",
      onClick: () => {
        navigate(
          companyPreferencesCreateHiringTemplatePath(company?.id, undefined, {
            name: t("exampleTemplate.name", {
              companyName: company?.name ?? "",
              ns: "hiring_templates/HiringTemplateCard",
            }),
            description: t("exampleTemplate.description", {
              ns: "hiring_templates/HiringTemplateCard",
            }),
            type: "global",
          }),
        )
      },
    },
  }
}
